import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, Shield, Users, Calculator, Home, ArrowRight, Star, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import AmortizationCalc from '../components/calculators/AmortizationCalc';
import MortgageCalc from '../components/calculators/MortgageCalc';
import PropertyValueCalc from '../components/calculators/PropertyValueCalc';
import RentVsBuyCalc from '../components/calculators/RentVsBuyCalc';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="home-page">
      <HeroSection navigate={navigate} />
      <ServicesSection />
      <CalculatorsSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
    </div>
  );
}

function HeroSection({ navigate }) {
  return (
    <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1757264119066-2f627c6a6f03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBkYXl8ZW58MHx8fHwxNzc2MTE2NTM3fDA&ixlib=rb-4.1.0&q=85"
          alt="Modern property"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-400 mb-6">Property Management & Real Estate</p>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05] mb-6">
            Sustainable Growth for Your Real Estate Portfolio
          </h1>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-10 max-w-lg">
            From property management to buying, selling, and investment consulting &mdash; 4 Longevity is your partner for long-term real estate success.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button
              className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-8 py-6 font-semibold text-base shadow-lg shadow-emerald-900/30 transition-all"
              onClick={() => navigate('/quote')}
              data-testid="hero-get-quote-btn"
            >
              Get a Free Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white rounded-full px-8 py-6 font-semibold text-base backdrop-blur-sm transition-all"
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-services-btn"
            >
              Our Services
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    {
      icon: Building2,
      title: "Property Management",
      desc: "Full-service management including tenant screening, maintenance coordination, rent collection, and financial reporting.",
      image: "https://images.unsplash.com/photo-1760067538022-8ef8739b1b18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBkYXl8ZW58MHx8fHwxNzc2MTE2NTM3fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: TrendingUp,
      title: "Buy & Sell Properties",
      desc: "Expert guidance through every step of buying or selling your property. Market analysis, negotiation, and closing support.",
      image: "https://images.unsplash.com/photo-1716078410207-20e223587181?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxicmlnaHQlMjBtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbSUyMGRheXxlbnwwfHx8fDE3NzYxMTY1Mzd8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Shield,
      title: "Investment Consulting",
      desc: "Strategic real estate investment advice. Portfolio analysis, market research, and ROI optimization for long-term growth.",
      image: "https://images.unsplash.com/photo-1768508661007-c19b6083a388?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxyZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjB0ZWFtJTIwcHJvZmVzc2lvbmFsJTIwZGF5fGVufDB8fHx8MTc3NjExNjU3Mnww&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <section id="services" className="py-24 sm:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">What We Offer</p>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
            Complete Real Estate Solutions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <div key={i} className="group bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300" data-testid={`service-card-${i}`}>
              <div className="h-48 overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-8">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-emerald-900" />
                </div>
                <h3 className="font-outfit text-xl font-semibold tracking-tight text-stone-900 mb-3">{s.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CalculatorsSection() {
  return (
    <section id="calculators" className="py-24 sm:py-32 bg-stone-50" data-testid="calculators-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">Free Tools</p>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
            Financial Calculators
          </h2>
          <p className="text-stone-600 mt-3 max-w-lg mx-auto">Plan your next move with our free real estate calculators.</p>
        </div>
        <Tabs defaultValue="amortization" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 h-auto bg-stone-100 rounded-2xl p-1.5 mb-8">
            <TabsTrigger value="amortization" className="rounded-xl py-2.5 text-xs sm:text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-amortization">
              <Calculator className="w-4 h-4 mr-1.5 hidden sm:inline" /> Amortization
            </TabsTrigger>
            <TabsTrigger value="mortgage" className="rounded-xl py-2.5 text-xs sm:text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-mortgage">
              <Home className="w-4 h-4 mr-1.5 hidden sm:inline" /> Mortgage
            </TabsTrigger>
            <TabsTrigger value="property" className="rounded-xl py-2.5 text-xs sm:text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-property">
              <TrendingUp className="w-4 h-4 mr-1.5 hidden sm:inline" /> Value Est.
            </TabsTrigger>
            <TabsTrigger value="rentvsbuy" className="rounded-xl py-2.5 text-xs sm:text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-rentvsbuy">
              <Users className="w-4 h-4 mr-1.5 hidden sm:inline" /> Rent vs Buy
            </TabsTrigger>
          </TabsList>
          <div className="max-w-2xl mx-auto">
            <TabsContent value="amortization"><AmortizationCalc /></TabsContent>
            <TabsContent value="mortgage"><MortgageCalc /></TabsContent>
            <TabsContent value="property"><PropertyValueCalc /></TabsContent>
            <TabsContent value="rentvsbuy"><RentVsBuyCalc /></TabsContent>
          </div>
        </Tabs>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const testimonials = [
    { name: "Sarah M.", role: "Property Owner", text: "4 Longevity has been managing my rental properties for 3 years. Consistent communication, reliable maintenance, and my tenants love them.", rating: 5 },
    { name: "James L.", role: "First-time Buyer", text: "They walked me through the entire home buying process. The investment analysis they provided helped me find the perfect property.", rating: 5 },
    { name: "Patricia K.", role: "Real Estate Investor", text: "Their consulting services helped me restructure my portfolio. I've seen 18% better returns since working with them.", rating: 5 },
  ];

  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">Testimonials</p>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
            What Our Clients Say
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-3xl border border-stone-200 p-8 shadow-sm" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-stone-600 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
              <div>
                <p className="font-outfit font-semibold text-stone-900">{t.name}</p>
                <p className="text-xs text-stone-500">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    { q: "What areas do you serve?", a: "We provide property management and real estate services across multiple regions. Contact us to confirm service availability in your area." },
    { q: "How do you screen tenants?", a: "We conduct comprehensive background checks including credit history, employment verification, rental history, and criminal background screening to ensure quality tenants for your properties." },
    { q: "What are your management fees?", a: "Our fees are competitive and tailored to the scope of services you need. Contact us for a personalized quote based on your specific property and requirements." },
    { q: "Can I track my property performance online?", a: "Yes! Once registered, landlords can access our portal to view financial reports, maintenance requests, and tenant communications in real time." },
    { q: "How do you handle maintenance requests?", a: "We have a 24/7 maintenance request system. Tenants submit requests through their portal, and we coordinate with trusted vendors to resolve issues promptly." },
    { q: "Do you help with buying investment properties?", a: "Absolutely. Our investment consulting team provides market analysis, property sourcing, due diligence, and ROI projections to help you make informed decisions." },
  ];

  return (
    <section id="faq" className="py-24 sm:py-32 bg-stone-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">FAQ</p>
          <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
            Frequently Asked Questions
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-stone-200 px-6 shadow-sm" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-outfit font-semibold text-stone-900 text-sm hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-stone-600 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-900/70 mb-3">Get In Touch</p>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900 mb-6">
              Let's Talk About Your Property
            </h2>
            <p className="text-stone-600 leading-relaxed mb-10">
              Whether you need property management, want to buy or sell, or are exploring investment opportunities, we're here to help.
            </p>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-emerald-900" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-stone-500">Phone</p>
                  <a href="tel:1-888-419-9767" className="text-stone-900 font-semibold hover:text-emerald-900 transition-colors">1-888-419-9767</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-emerald-900" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-stone-500">General</p>
                  <a href="mailto:general@4longevity.live" className="text-stone-900 font-semibold hover:text-emerald-900 transition-colors">general@4longevity.live</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-emerald-900" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-stone-500">Quotes</p>
                  <a href="mailto:quotes@4longevity.live" className="text-stone-900 font-semibold hover:text-emerald-900 transition-colors">quotes@4longevity.live</a>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-stone-50 rounded-3xl p-8 border border-stone-200">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="bg-white text-stone-900" data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="bg-white text-stone-900" data-testid="contact-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} required className="bg-white text-stone-900" data-testid="contact-subject" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Message</Label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  required
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-stone-900 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="contact-message"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full py-6 font-semibold shadow-lg shadow-emerald-900/20 transition-all" data-testid="contact-submit">
                Send Message
              </Button>
              {status === 'success' && <p className="text-emerald-700 text-sm text-center" data-testid="contact-success">Message sent successfully!</p>}
              {status === 'error' && <p className="text-red-600 text-sm text-center" data-testid="contact-error">Failed to send. Please try again.</p>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
